import { isLocalStorageAvailable } from "@kaggle/core";
import { FirebaseApp, getApp, initializeApp, setLogLevel } from "firebase/app";
import {
  getAuth,
  signInAnonymously,
  signInWithCustomToken,
} from "firebase/auth";
import { getFirestore, initializeFirestore } from "firebase/firestore";
import { featureFlags } from "../featureFlags";

const apps = new Map<string, Promise<FirebaseApp>>();

const daysSinceUnixMillis = (then: number) => {
  const now = new Date();
  return (now.getTime() - then) / (1000 * 3600 * 24);
};

export interface FirebaseConfig {
  authToken: string | null;
  options: FirebaseOptions | null;
  proxyDomain: string | null;
}

export interface FirebaseOptions {
  appId: string | null;
  apiKey: string | null;
  authDomain: string | null;
  databaseUrl: string | null;
  messagingSenderId: string | null;
  projectId: string | null;
  storageBucket: string | null;
}

export type ConfigFetcherFunc = () => Promise<FirebaseConfig>;
export type OnFirestoreConnectionError = (errorMsg: string) => void;

export const ensureClientInitialized = (
  appName: string,
  firebaseConfig: FirebaseConfig,
  onFirebaseConnectionError?: OnFirestoreConnectionError
) => {
  if (onFirebaseConnectionError) {
    errorDispatcher.addListener(appName, onFirebaseConnectionError);
  }
  if (!apps.has(appName)) {
    apps.set(appName, initializeClient(appName, firebaseConfig));
  }
  return apps.get(appName)!;
};

const initializeClient = async (
  appName: string,
  firebaseConfig: FirebaseConfig
) => {
  const useFirestoreProxy =
    featureFlags.kernelsFirebaseProxy ||
    (isLocalStorageAvailable() &&
      !!localStorage.useProxy &&
      daysSinceUnixMillis(localStorage.useProxyUpdateTime) < 1); // expire proxy failover after 1 day

  let app: FirebaseApp;
  try {
    // Throws an exception if the app hasn't been initialized yet.
    app = getApp(appName);
  } catch {
    app = initializeApp(
      {
        // Convert null to undefined.
        apiKey: firebaseConfig.options?.apiKey || undefined,
        appId: firebaseConfig.options?.appId || undefined,
        authDomain: firebaseConfig.options?.authDomain || undefined,
        messagingSenderId:
          firebaseConfig.options?.messagingSenderId || undefined,
        projectId: firebaseConfig.options?.projectId || undefined,
        storageBucket: firebaseConfig.options?.storageBucket || undefined,
      },
      appName
    );
  }

  if (featureFlags.kernelsFirestoreDebugLogging) {
    setLogLevel("debug");
  }

  const auth = getAuth(app);
  if (useFirestoreProxy && firebaseConfig.proxyDomain) {
    // Proxy auth-related calls.
    auth.config.apiHost = firebaseConfig.proxyDomain; // Proxy calls to identitytoolkit.googleapis.com
    auth.config.tokenApiHost = firebaseConfig.proxyDomain; // Proxy calls to securetoken.googleapis.com
    auth.config.authDomain = firebaseConfig.proxyDomain; // Proxy calls to <database>.firebaseapp.com (database is inferred from api key)

    initializeFirestore(app, {
      host: firebaseConfig.proxyDomain,
      experimentalForceLongPolling: featureFlags.kernelsFirebaseLongPolling,
    });
  }

  const failover = (e?: any) => {
    if (!useFirestoreProxy) {
      if (isLocalStorageAvailable()) {
        // Try to reload the page with proxying turned on:
        localStorage.useProxy = true;
        const now = new Date();
        localStorage.useProxyUpdateTime = now.getTime();
        location.reload();
        return;
      }
      errorDispatcher.dispatch(
        appName,
        "Failed to connect to Firestore. " + (e ?? "")
      );
      return;
    }
    errorDispatcher.dispatch(
      appName,
      "Failed to connect to Firestore via Jupyter. " + (e ?? "")
    );
  };

  try {
    firebaseConfig.authToken && firebaseConfig.authToken.length > 0
      ? await signInWithCustomToken(auth, firebaseConfig.authToken)
      : await signInAnonymously(auth);
    const cli = getFirestore(app);
    window.setTimeout(() => {
      const status = (cli as any)._firestoreClient?.onlineComponents?.syncEngine
        ?.onlineState;
      if (status !== "Online") {
        failover();
      }
    }, 10000);
  } catch (e) {
    if (e.code && e.code === "auth/network-request-failed") {
      failover(e);
    } else {
      errorDispatcher.dispatch(appName, "Failed to connect to Firestore: " + e);
    }
    throw e;
  }
  return app;
};

class FirebaseConnectionErrorDispatcher {
  private listeners = new Map<string, Array<OnFirestoreConnectionError>>();

  addListener = (appName: string, listener: OnFirestoreConnectionError) => {
    let listenersForApp = this.listeners.get(appName);
    if (!listenersForApp) {
      listenersForApp = new Array<OnFirestoreConnectionError>();
      this.listeners.set(appName, listenersForApp);
    }
    listenersForApp.push(listener);
  };

  dispatch = (appName: string, errorMsg: string) => {
    const listenersForApp = this.listeners.get(appName);
    if (listenersForApp) {
      for (const listener of listenersForApp) {
        listener(errorMsg);
      }
    }
  };
}

const errorDispatcher = new FirebaseConnectionErrorDispatcher();
